<template>
  <side-panel-inner headerTitle="Submit a Question" class="p-form-create">
    <template slot="actions">
      <base-button @click="onSave" iconBefore="save" :iconBeforeFill="null">
        Save
        <span class="u-hide-mobile">as Draft</span>
      </base-button>
      <base-button @click="onPublish" :primary="true" iconBefore="pencil">Publish</base-button>
    </template>

    <div
      v-if="editMode && ($store.state.auth && question.user && question.user.id !== $store.state.auth.currentUser.id) && !userCanEdit"
    >
      <h1>Permission denied</h1>
      <p>Sorry, you do not have permission to edit this question.</p>
    </div>

    <loading v-else-if="loading"/>

    <!-- Policy Title -->
    <form v-else-if="!loading" action>
      <message type="error" v-if="showValidationMessage">
        <h3>Form Invalid</h3>
        <p>Form is invalid, please check and try again.</p>
        <ul>
          <li v-for="error in errors.items" :key="error.id">{{error.msg}}</li>
        </ul>
      </message>

      <form-field
        full-width
        required
        label="Question Title"
        name="question_title"
        :max-length="45"
        show-counter
        helper-text="Enter your question title here. Good questions are short and straight to the point, you can elaborate on your question in the next box"
        v-validate="'required'"
        data-vv-as="question title"
        :error="errors.first('question_title')"
        v-model="question.title"
      ></form-field>

      <form-field
        label="Additional Information"
        name="description"
        helper-text="Give more information about why your have asked this question and what ideas you are expecting to receive"
        :required="true"
      >
        <quill-editor
          :options="quillEditorOptions"
          v-model="question.description"
          v-validate="'required'"
          data-vv-as="description"
          name="description"
        ></quill-editor>
        <p
          class="c-form__validation-message"
          v-if="errors.first('description')"
        >{{errors.first('description')}}</p>
      </form-field>

      <p>
        <base-button
          type="button"
          :asLink="true"
          iconBefore="plus-circle"
          @click="toggleAttachments"
        >Add attachments</base-button>
      </p>

      <file-upload
        name="attachment"
        :fileId="null"
        :bypassDuplicate="true"
        :fileTypes="['png','pdf','doc','docx','xls','xlsx','gif','jpg']"
        v-if="addFile"
        @uploaded="onFileUploaded"
        :showExtensions="false"
        :multiple="true"
      ></file-upload>

      <form-field
        label="Interest Groups"
        name="interest_groups"
        helper-text="Help users find your question by tagging related interest groups:"
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="question.interestgroups"
          :options="interestGroups"
          :multiple="true"
          label="name"
          track-by="name"
          :taggable="true"
        ></form-multiselect>
      </form-field>

      <form-field
        label="Choose Closing Date"
        name="closing_date"
        helper-text="Select your idea closing date"
      >
        <form-datepicker
          small
          v-model="question.end_date"
          name="end_date"
          format="DD / MM / YYYY"
          input-class="c-form__input"
          data-vv-as="end_date"
          :error="errors.first('end_date')"
          :disabledDates="disabledDates"
          @input="onEndDateInput"
        ></form-datepicker>
      </form-field>

      <form-field
        label="Allow Voting"
        name="allow_voting"
        helper-text="Allow all users to vote on their favourite ideas put forward for your question?"
      >
        <form-toggle v-model="question.voting" @toggle="handleVoteToggle"/>
      </form-field>

      <form-field
        label="Make question private?"
        name="private"
        helper-text="Do you want to lock down your question to specific users?"
      >
        <form-toggle v-model="question.private" @toggle="handlePrivateToggle"/>
      </form-field>

      <user-group-select @onChange="onUserAdd" v-if="question.private" :value="selectedUsers"/>

      <form-field
        v-if="question.private"
        full-width
        label="Name your group"
        name="usergroup_name"
        helper-text="If you wish to use this bespoke group in the future, please name it:"
        v-model="usergroup.name"
      ></form-field>
    </form>
  </side-panel-inner>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
// import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
// import 'quill/dist/quill.bubble.css'
import FileUpload from "@/components/FileUpload";
import { FormToggle, UserGroupSelect } from "@/components/forms";
import { mapState } from "vuex";
import UserHasPermission from "@/mixins/UserHasPermission";

export default {
  name: "question-create",

  mixins: [UserHasPermission],

  data() {
    return {
      showValidationMessage: false,
      selected_users: [],
      loading: true,
      resourceCreated: false,
      addFile: false,
      editMode: false,
      usergroup: {
        name: null
      },

      responseData: {
        resourceId: null
      },
      permissions: ["comment_on_ideas"],
      quillEditorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"]
          ],
          clipboard: {
            matchVisual: false
          }
        },
        formats: ["bold", "italic", "underline", "header", "list", "link"]
      }
    };
  },

  methods: {
    handlePrivateToggle(val) {
      this.question.private = val;
    },
    onUserAdd(value) {
      this.selected_users = [];
      this.question.users = [];
      value.forEach(val => {
        //if we are a group, we need to extract the individual user ids
        if (val.id.toString().includes("grp")) {
          val = val.meta.data;
          this.question.users = [...this.question.users, ...val];
        } else {
          this.question.users.push(val);
        }
        this.selected_users.push(val);
      });
    },

    handleVoteToggle(val) {
      this.question.voting = val;
    },
    onEndDateInput(val) {
      this.question.end_date = this.$dayjs(val).format("YYYY-MM-DD");
    },
    descErrorString() {},
    onSave() {
      // TODO: Same as below, but for saving as a draft.  Just update the status?
      this.onPublish(false);
    },
    onPublish(makeLive = true) {
      if (makeLive) {
        this.question.status = "publish";
      }

      const vm = this;
      this.loading = true;
      // we only want to validate if we are not saving
      this.$validator.validate().then(result => {
        if (!result && vm.question.status !== "draft") {
          vm.showValidationMessage = true;
          this.loading = false;
        } else {
          this.loading = true;
          //also just need ids for groups
          if (vm.question.interestgroups) {
            vm.question.interestgroup_ids = vm.question.interestgroups.map(
              cat => cat.id
            );
          }
          let endpoint = "Questions/create";
          let payload = vm.question;

          if (this.editMode) {
            //for editing, we only need a specific payload
            endpoint = "Questions/update";
          } else {
            let endpoint = "Questions/create";
          }

          //lets determine the user objects
          let userIds = [];
          if (vm.question.users) {
            userIds = vm.question.users.map(usr => {
              if (typeof usr === "number") {
                return usr;
              }
              return usr.id;
            });
          }
          payload = {
            id: vm.question.id,
            title: vm.question.title,
            description: vm.question.description,
            private: vm.question.private,
            voting: vm.question.voting,
            end_date: vm.question.end_date,
            status: vm.question.status,
            interestgroup_ids: vm.question.interestgroups
              ? vm.question.interestgroups.map(group => group.id)
              : [],
            file_ids: vm.question.file_ids,
            user_ids: userIds
          };

          let progressWithCreate = true;

          // Do we have a group name and users?  If so, create a new group here
          // TODO: Should we try and create the group first?
          if (this.usergroup.name && payload.user_ids.length > 0) {
            // Do we need to rename?

            let groupPayload = {
              name: this.usergroup.name,
              description: "",
              users: payload.user_ids
            };

            this.$store
              .dispatch("UserGroups/create", groupPayload)
              .then(grpResp => {
                vm.$store.dispatch(
                  "notifications/create",
                  {
                    body: `Your user group was created`,
                    type: "success"
                  },
                  { root: true }
                );
                progressWithCreate = true;
              })
              .catch(err => {
                vm.$store.dispatch(
                  "notifications/create",
                  {
                    body: `Your user group was not created.  Please make sure the name is unique`,
                    type: "error"
                  },
                  { root: true }
                );
                progressWithCreate = false;
                this.loading = false;
              });
          }

          if (progressWithCreate) {
            vm.$store
              .dispatch(endpoint, payload)
              .then(resp => {
                const questionId = resp.data.data.question[0].id;

                //are we publishing?
                if (makeLive) {
                  vm.$store.dispatch("Questions/publish", questionId);
                }
                // Question created, notify and go to the question page
                vm.$store.dispatch(
                  "notifications/create",
                  {
                    title: "Success!",
                    body: `Your question was sucessfully ${
                      vm.question.status == "draft" ? "saved" : "published"
                    }`,
                    type: "success"
                  },
                  { root: true }
                );
                this.$store.dispatch("CLOSE_PANEL");
              })
              .catch(err => {
                console.log("error publisjing", { ...err });
                // Error - notify the user
                this.loading = false;
              });
          }
        }
      });
    },
    toggleAttachments() {
      this.addFile = !this.addFile;
    },
    onFileUploaded(val) {
      this.question.file_ids.push(val.id);
    }
  },

  computed: {
    ...mapState("Questions", ["question", "responses"]),
    ...mapState({
      interestGroups: state => state.InterestGroups.collection
    }),
    disabledDates() {
      let yesterday;
      yesterday = this.$dayjs()
        .subtract(1, "day")
        .toDate();

      return { to: yesterday };
    },
    selectedUsers: {
      get() {
        return this.question.users;
      },
      set(val) {
        return val;
      }
    },
    userCanEdit() {
      return this.userHasPermission("add_edit_publish_questions");
    }
  },

  components: {
    FileUpload,
    UserGroupSelect,
    FormToggle,
    'quill-editor':quillEditor
  },

  beforeCreate() {
    // this.$store.dispatch("forms/retrieveCategories");
  },

  created() {
    this.$store.dispatch("tags/list");
    this.$store.dispatch("Questions/resetState");
    this.$store.dispatch("InterestGroups/list");
    if (this.$route.params.questionId) {
      this.$store
        .dispatch("Questions/read", this.$route.params.questionId)
        .then(() => {
          this.loading = false;
        });
    } else {
      this.loading = false;
    }
    if (this.$route.params.questionId) {
      this.editMode = true;
    }
  }
};
</script>


<style lang="scss" scoped>
.p-form-create {
  @media screen and (min-width: 1024px) {
    background-image: url(/assets/qmark.svg);
    background-position: 0 100px;
    background-attachment: scroll;

    form {
      margin-left: 70px;
    }
  }
}
</style>

<style lang="scss">
.ql-toolbar {
  background: $colour-nhs-pale-grey;
}
.ql-container {
  background: $colour-nhs-core-white;
  font-size: 16px;

  @media (min-width: 768px) {
    font-size: 13px;
  }
}
</style>
